import TileLayer from "ol/layer/Tile";
import TileSource from "ol/source/Tile";
import VectorLayer from "ol/layer/Vector";
import {Feature, MapBrowserEvent, MapEvent} from "ol";
import {OSM, TileWMS, XYZ} from "ol/source";
import VectorSource from "ol/source/Vector";
import ClusterSource from "ol/source/Cluster";
import OlMap from 'ol/Map';
import OlView from 'ol/View';
import {WKT, WMSCapabilities} from "ol/format";
import {fromLonLat, getTransform, toLonLat, TransformFunction} from "ol/proj";
import {Geometry, Point} from "ol/geom";
import {Segnalazione_Firestore} from "../models/firestore/segnalazioneFirestore.model";
import OlStyle from "ol/style/Style";
import OlFill from "ol/style/Fill";
import OlStroke from "ol/style/Stroke";
import OlCircle from "ol/style/Circle";
import OlIcon from "ol/style/Icon";
import OlText from "ol/style/Text";
import {Sostegno} from "../models/sostegno.models";
import {createEmpty, isEmpty, extend, boundingExtent} from 'ol/extent';
import {InterventoSegnalazione_FireStore} from "../models/firestore/interventosegnalazionefirestore.model";
import {Layer} from "ol/layer";
import {
  PRIORITY_INTERVENTI_STATO,
  PRIORITY_STATO_SEGNALAZIONI,
  STILIMAPPA_INTERVENTI_STATO,
  STILIMAPPA_SEGNALAZIONI_STATO
} from "../shared/stili";
import {ComuneMappa} from "../models/territori_api";
import {PosizioneSegnalazione} from "../models/posizione-segnalazione";
import {QuadroElettrico} from "../models/quadro.model";
import { Edificio } from "../models/edificio.model";


export class ActivitiesMapWidget {

  bglayers: TileLayer<TileSource>[];

  backgroundLayer: TileLayer<TileSource>;
  territoryLayer: VectorLayer;
  segnalazioniLayer: VectorLayer<ClusterSource<Feature>>;
  interventiLayer: VectorLayer<ClusterSource<Feature>>;
  sostegniLayer: VectorLayer;
  quadriLayer: VectorLayer;
  positionLayer: VectorLayer;
  edificiLayer: VectorLayer;

  mapwidget: OlMap;
  mapview: OlView;

  selectionSostegni: string[];
  selectionEdifici: number[];
  showOnlyActiveSostegni: boolean;

  // per layer hack to get around clustered layers labels getting over other symbols
  // separating per layer so we can reset when needed
  zindexSegnalazioni: number;
  zindexInterventi: number;

  zoomCurrent: number;
  static MIN_ZOOM_LABELS = 12;

  transformDegWeb: TransformFunction;

  private listeners: ActivitiesMapEventListener[];

  private edifici: Edificio[] = [];

  constructor(private territories: ComuneMappa[], private sostegni: Sostegno[], private quadri: QuadroElettrico[], private enablePositionSelect: boolean = false) {
    this.transformDegWeb = getTransform('EPSG:4326', "EPSG:3857");
    this.bglayers = [];
    this.listeners = [];
    this.selectionSostegni = [];
    this.selectionEdifici = [];

    this.zindexSegnalazioni = 0;
    this.zindexInterventi = 0;

    this.showOnlyActiveSostegni = false;
    this.loadBackgroundLayers();

    // console.log("rendering w quadri", quadri);
  }

  addListener(listener: ActivitiesMapEventListener) {
    if (this.listeners.indexOf(listener) == -1) {
      this.listeners.push(listener);
    }
  }

  removeListener(listener: ActivitiesMapEventListener) {
    if (this.listeners.indexOf(listener) != -1) {
      this.listeners.splice(this.listeners.indexOf(listener), 1);
    }
  }

  addBackgroundLayer(layer: TileLayer<TileSource>) {
    this.bglayers.push(layer);
    this.notifyEvent(ActivitiesMapEvents.LAYERS_CHANGE);
  }

  setEdifici(edifici: Edificio[]) {
    this.edifici = edifici || [];
  }

  loadBackgroundLayers () {

    const osmLayer = new TileLayer<TileSource>({
      source: new OSM(),
    });
    osmLayer.set("name", "OpenStreetmap");

    const googleLayer = new TileLayer({
      source: new XYZ({
        url: "https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}"
      })
    });
    googleLayer.set("name", "Google Satellite");

    this.bglayers = [
      osmLayer,
      googleLayer
    ];

  }

  setBgLayerByName (name: string) {
    const bglayer = this.bglayers.find((item) => {
      return item.get("name") == name;
    });
    if (!!bglayer) {
      this.mapwidget.removeLayer(this.backgroundLayer);
      this.backgroundLayer = bglayer;
      this.mapwidget.getLayers().insertAt(0, this.backgroundLayer);
    }
  }

  loadLombardyWMSLayer () {

    const parser = new WMSCapabilities();
    const url = "https://www.cartografia.servizirl.it/arcgis2/services/BaseMap/Ortofoto2021/ImageServer/WMSServer?request=GetCapabilities";


    fetch(url).then((response: Response) => {
      response.text().then((data: string) => {
        const caps = parser.read(data);
        // console.log("caps parsed", caps);
        const layer = new TileLayer ({
          source: new TileWMS ({
            url: caps["Service"]["OnlineResource"],
            params: {'LAYERS': caps["Capability"]["Layer"]["Name"], 'TILED': true},
          })
        });
        layer.set("name", caps["Service"]["Title"] ?? "Ortofoto 2021");
        this.addBackgroundLayer(layer);
      })
    })


  }

  getViewExtent() {
    const source = this.territoryLayer.getSource();
    const padding = source.getFeatures().length > 1 ? 5000 : 1000;
    const ext = source.getExtent();
    ext[0] -= padding;
    ext[1] -= padding;
    ext[2] += padding;
    ext[3] += padding;
    return ext;
  }

  fitToData() {
    const padding = 5000;
    let ext = createEmpty();

    ext = extend(ext, this.segnalazioniLayer.getSource().getExtent());
    ext = extend(ext, this.interventiLayer.getSource().getExtent());

    if (!isEmpty(ext)) {
      // console.log("changing extent to", [...ext]);
      this.mapview.fit(ext);
    }
  }

  initMap(targetdiv: string, readonly: boolean = false) {

    // console.log("init mapwidget inner on " + targetdiv);

    this.loadLombardyWMSLayer();

    this.backgroundLayer = this.bglayers[0];

    //TODO: Violazione del principio DRY e dell'open/closed principle, va bene o è meglio creare un metodo generico che prende in ingresso i dati e lo stile e aggiorna il layer di conseguenza?

    this.territoryLayer = new VectorLayer({
      source: new VectorSource(),
      style: this.getStyleTerritories()
    });
    if (this.territories.length > 0) {
      this.updateTerritories();
    }

    this.sostegniLayer = new VectorLayer({
      source: new VectorSource(),
      style: this.getStyleSostegni(),
      declutter: true
    });
    if (this.sostegni.length > 0) {
      this.updateSostegni();
    }

    this.quadriLayer = new VectorLayer({
      source: new VectorSource(),
      style: this.getStyleQuadri(),
      // declutter: true
    });
    if (this.quadri.length > 0) {
      this.updateQuadri();
    }

    this.edificiLayer = new VectorLayer({
      source: new VectorSource(),
      style: this.getStyleEdifici()
    });
    if (this.edifici.length > 0) {
      this.updateEdifici();
    }


    this.segnalazioniLayer = new VectorLayer({
      source: new ClusterSource ({
        source: new VectorSource()
      }),
      style: this.getStyleSegnalazioni()
    });

    this.interventiLayer = new VectorLayer({
      source: new ClusterSource({
        source: new VectorSource()
      }),
      style: this.getStyleInterventi()
    });

    this.positionLayer = new VectorLayer({
      source: new VectorSource(),
      style: this.getStylePositionPin()
    });

    const viewExtent = this.getViewExtent();

    this.mapview = new OlView({
      zoom: 2, maxZoom: 28,
      extent: viewExtent,
      showFullExtent: true
    });

    this.mapwidget = new OlMap({
      view: this.mapview,
      target: targetdiv,
      layers: [
        this.backgroundLayer,
        this.territoryLayer,
        this.quadriLayer,
        this.sostegniLayer,
        this.segnalazioniLayer,
        this.interventiLayer,
        this.positionLayer,
        this.edificiLayer
      ]
    });

    this.mapview.fit(viewExtent, {
      nearest: true
    });

    this.mapwidget.on("click", (event: MapBrowserEvent<any>) => {

      let selSostegno: Sostegno = null;
      let selSegnalazione: Segnalazione_Firestore = null;
      let selIntervento: InterventoSegnalazione_FireStore = null;
      let selPosizione: boolean = false;
      let selEdificio: Edificio = null;
      this.mapwidget.forEachFeatureAtPixel(event.pixel, (feature: Feature, layer: Layer) => {
        //TODO: avoid this wall of if [violated o/c principle, ok for 5/6 case]
        if (layer == this.sostegniLayer) {
          selSostegno = feature.getProperties()["src"];
          return true;
        } else if (layer == this.positionLayer) {
          selPosizione = true;
          return true;
        } else if (layer == this.segnalazioniLayer) {
          const clustered = feature.getProperties()["features"];
          if (clustered.length == 1) {
            selSegnalazione = clustered[0].getProperties()["src"];
          }
          return true;
        } else if (layer == this.interventiLayer) {
          const clustered = feature.getProperties()["features"];
          if (clustered.length == 1) {
            selIntervento = clustered[0].getProperties()["src"];
          }
          return true;
        } else if (layer == this.edificiLayer) {
          selEdificio = feature.getProperties()["src"];
          return true;
        }
        return false;

      });

      if (selSostegno != null) {
        this.notifyEvent(ActivitiesMapEvents.SELECT_SOSTEGNO, selSostegno);
      } else if (selSegnalazione != null) {
        this.notifyEvent(ActivitiesMapEvents.SELECT_SEGNALAZIONE, selSegnalazione);
      } else if (selIntervento != null) {
        this.notifyEvent(ActivitiesMapEvents.SELECT_INTERVENTO, selIntervento);
      } else if (selPosizione && this.enablePositionSelect) {
        this.selectActivePosition(null);
        this.notifyEvent(ActivitiesMapEvents.SELECT_POSITION, null);
      }else if (selEdificio) {
        this.notifyEvent(ActivitiesMapEvents.SELECT_EDIFICIO, selEdificio); 
      } else if (this.enablePositionSelect) {
        const lonlat = toLonLat([...event.coordinate]);
        this.selectActivePosition(lonlat);
        this.notifyEvent(ActivitiesMapEvents.SELECT_POSITION, lonlat);
      }

    });

    this.mapwidget.on("movestart", (event: MapEvent) => {
      this.notifyEvent(ActivitiesMapEvents.MOVE_STATE, true);
    });

    this.zoomCurrent = this.mapview.getZoom();
    // console.log("starting at zoom ", this.zoomCurrent);
    this.mapwidget.on("moveend", (event: MapEvent) => {
      this.notifyEvent(ActivitiesMapEvents.MOVE_STATE, false);
      const nzoom = this.mapview.getZoom();
      if (nzoom != this.zoomCurrent) {
        this.zoomCurrent = nzoom;
        this.notifyEvent(ActivitiesMapEvents.ZOOM_CHANGE, nzoom);
      }

    });

    this.notifyEvent(ActivitiesMapEvents.READY_STATE, true);

  }

  getPixelPositionForCoords (lonlat: number[]): number[] {
    return this.mapwidget.getPixelFromCoordinate(fromLonLat(lonlat));
  }


  updateTerritories() {
    const source = this.territoryLayer.getSource();
    // const reader = new GeoJSON();
    const reader = new WKT();
    const features = [];
    for (let comunemappa of (this.territories ?? [])) {
      const feature: Feature<Geometry> = reader.readFeature(comunemappa.geom) as Feature;
      feature.getGeometry().applyTransform(this.transformDegWeb);
      feature.set("src", comunemappa);
      features.push(feature);
    }
    //console.log("territories", features);
    source.clear();
    source.addFeatures(features);
    source.changed();
  }

  updateEdifici() {
    const source = this.edificiLayer.getSource();
    // const reader = new GeoJSON();
    const reader = new WKT();
    const features = [];
    for (let edificio of this.edifici) {
      const feature: Feature<Geometry> = reader.readFeature(edificio.geom) as Feature;
      feature.getGeometry().applyTransform(this.transformDegWeb);
      feature.set("src", edificio);
      features.push(feature);
    }
    //console.log("territories", features);
    source.clear();
    source.addFeatures(features);
    source.changed();
  }

   updateQuadri () {
    const source = this.quadriLayer.getSource();
    const features = [];
    for (let quadro of (this.quadri ?? [])) {
      const geom = new Point([quadro.cordLong, quadro.cordLat]);
      geom.applyTransform(this.transformDegWeb);
      console.log("feature quad", quadro, "moved at ", geom.getCoordinates());
      const feature = new Feature(geom);
      feature.set("src", quadro);
      features.push(feature);
    }
    // console.log("feature quadri", features.map(item => item.getGe));
    source.clear();
    source.addFeatures(features);
    source.changed();

  }

  updateSostegni() {
    const source = this.sostegniLayer.getSource();
    const features = [];
    for (let sostegno of (this.sostegni ?? [])) {
      const geom = new Point([sostegno.longitudine, sostegno.latitudine]);
      geom.applyTransform(this.transformDegWeb);
      console.log("feature sost", sostegno, "moved at ", geom.getCoordinates());
      const feature = new Feature(geom);
      feature.set("src", sostegno);
      features.push(feature);
    }

    source.clear();
    source.addFeatures(features);
    source.changed();

  }

  updateInterventi(interventi: InterventoSegnalazione_FireStore[]) {
    const source = this.interventiLayer.getSource().getSource();
    const features = [];
    for (let intervento of (interventi ?? [])) {
      if (!intervento.segnalazione) {
        // nessun log in questo caso perché spesso è un problema di allineamento dei dati tra segnalazioni e interventi che vengono aggiornati separatmente da code Firebase differenti
        continue;
      }
      try {
          const geom = new Point([intervento.segnalazione.coordinate.longitude, intervento.segnalazione.coordinate.latitude]);
          geom.applyTransform(this.transformDegWeb);
          const feature = new Feature(geom);
          feature.set("src", intervento);
          features.push(feature);
      } catch (err) {
        // console.log("errore rendering intervento ", intervento, ""+err);
        // console.error(err);
      }

    }

    // console.log("interventi", interventi);
    // console.log("interventi features", features, features.map((feature) => feature.get("src").intervento.stato));
    // console.log("intsig coords", features.map((feature) => [
    //   feature.getGeometry().flatCoordinates,
    //   feature.get("src")
    // ]));

    source.clear();
    this.zindexInterventi = 0;
    source.addFeatures(features);
    source.changed();

  }


  updateSegnalazioni(segnalazioni: Segnalazione_Firestore[]) {
    const source = this.segnalazioniLayer.getSource().getSource();
    const features = [];
    for (let segnalazione of (segnalazioni ?? [])) {
      try {
          const geom = new Point([segnalazione.coordinate.longitude, segnalazione.coordinate.latitude]);
          geom.applyTransform(this.transformDegWeb);
          const feature = new Feature(geom);
          feature.set("src", segnalazione);
          features.push(feature);
      } catch (err) {
        // console.warn("errore rendering segnalazione: ", segnalazione, "" + err);
        // console.error(err);
      }

    }

    // console.log("segnalazioni features", features, features.map((feature) => feature.get("src").stato));


    source.clear();
    this.zindexSegnalazioni = 0;
    source.addFeatures(features);
    source.changed();


  }

  getStyleTerritories() {
    return (feature: Feature, resolution: number) => {
      // console.log("Territory feature ", feature, feature.getProperties());

      let styleText = null;
      if (this.mapview.getZoom() >= ActivitiesMapWidget.MIN_ZOOM_LABELS) {
        styleText = new OlText({
          text: feature.getProperties()["src"]["comune"],
          font: "bold 12pt sans-serif",
          fill: new OlFill ({
            color: "#ffffff"
          }),
          stroke: new OlStroke({
            width: 3,
            color: "#000000"
          }),
          overflow: true
        });
      }

      // console.log("stiletext", styleText );
      return [
        new OlStyle({
          stroke: new OlStroke({
            color: "#ffffff",
            width: 5
          }),
        }),
        new OlStyle({
          stroke: new OlStroke({
            color: "rgba(14,43,82,0.8)",
            width: 3
          }),
          text: styleText
        })

      ];

    }
  }

  getStyleSegnalazioni() {

    return (feature: Feature, resolution: number) => {

      const zindex = this.zindexSegnalazioni++;
      const features: Feature[] = (feature.get("features") ?? []);
      if (features.length > 1) {

        // styling clustered features picking color from most "important" state
        const index_stati: number[] = features.map((f) => {
          const stato_inner = (f.getProperties()["src"] as Segnalazione_Firestore).stato;
          return PRIORITY_STATO_SEGNALAZIONI.indexOf(stato_inner);
        });
        const stato_gruppo = PRIORITY_STATO_SEGNALAZIONI[Math.min(...index_stati)];

        const styledict = STILIMAPPA_SEGNALAZIONI_STATO[stato_gruppo] ?? {};
        // console.log(styledict, stato_gruppo, features.map((item) => {
        //   return item.getProperties()["src"];
        // }));
        const symbol = new OlCircle({
            radius: 18,
            fill: new OlFill({
              color: styledict["fill"] ?? "#ffff00",
            }),
            stroke: new OlStroke({
              color: "#000000",
              width: 2,
            }),
          });
        return new OlStyle({
          image: symbol,
          text: new OlText({
            text: `${features.length}`,
            font: 'bold 16px sans-serif',
            stroke: new OlStroke({
              color: styledict["stroke"] ?? "#000000",
            }),
          }),
          zIndex: zindex
        });
      } else if (features.length == 1) {
        // styling single feature by its own state
        const sig = features[0].getProperties()["src"] as Segnalazione_Firestore;
        const styledict = STILIMAPPA_SEGNALAZIONI_STATO[sig.stato];
        let symbol: OlIcon | OlCircle;
        if (styledict["icon"]) {
          symbol = new OlIcon({
            src: styledict["icon"],
            width: 32,
            anchor: [0.5, 1]
          });
        } else {
          symbol = new OlCircle({
            radius: 12,
            fill: new OlFill({
              color: styledict["fill"],
            }),
            stroke: new OlStroke({
              color: "#000000",
              width: 2,
            }),
          });
        }

        return new OlStyle({
          image: symbol,
          zIndex: zindex
        });

      } else {
        return null;
      }
    }
  }


  getStyleInterventi () {

    return (feature: Feature, resolution: number) => {

      const zindex = this.zindexInterventi++;
      const features: Feature[] = (feature.get("features") ?? []);
      if (features.length > 1) {
        // styling clustered features picking color from most "important" state
        const index_stati: number[] = features.map((f) => {
          const stato_inner = (f.getProperties()["src"] as InterventoSegnalazione_FireStore).intervento.stato;
          return PRIORITY_INTERVENTI_STATO.indexOf(stato_inner);
        });
        const stato_gruppo = PRIORITY_INTERVENTI_STATO[Math.min(...index_stati)];
        const styledict = STILIMAPPA_INTERVENTI_STATO[stato_gruppo];
        const symbol = new OlCircle({
            radius: 18,
            fill: new OlFill({
              color: styledict["fill"] ?? "#ffff00",
            }),
            stroke: new OlStroke({
              color: "#000000",
              width: 2,
            }),
          });
        return new OlStyle({
          image: symbol,
          text: new OlText({
            text: `${features.length}`,
            font: 'bold 16px sans-serif',
            stroke: new OlStroke({
              color: styledict["stroke"] ?? "#000000",
            }),
          }),
          zIndex: zindex
        });

      } else if (features.length == 1) {

        const sigint = features[0].getProperties()["src"] as InterventoSegnalazione_FireStore;
        const styledict = STILIMAPPA_INTERVENTI_STATO[sigint.intervento.stato];
        let symbol: OlIcon | OlCircle;
        if (styledict["icon"]) {
          symbol = new OlIcon({
            src: styledict["icon"],
            width: 32,
            anchor: [0.5, 1]
          });
        } else {
          symbol = new OlCircle({
            radius: 12,
            fill: new OlFill({
              color: styledict["fill"] ?? "#ffff00",
            }),
            stroke: new OlStroke({
              color: "#000000",
              width: 2,
            }),
          });
        }

        return new OlStyle({
          image: symbol,
          zIndex: zindex
        });

      } else {
        return null;
      }
    }
  }

  getStyleQuadri() {
    return (feature: Feature, resolution: number) => {
      const sig = feature.getProperties()["src"] as QuadroElettrico;

      const isSelected = false; // in case it gets added later on

      const symbol = new OlIcon({
        src: "/assets/icons/pin_ates_quadri.svg",
        width: 48,
        anchor: [0.5, 1],
        declutterMode: "none"
      });

      // console.log("res", resolution);
      const zIndex = isSelected ? 15000: 5000;
      const hasText = isSelected || resolution < 0.5;

      return new OlStyle({
        image: symbol,
        zIndex: zIndex,
        });

/*      if (hasText) {
        styleOutput.unshift(new OlStyle({
          text: new OlText({
            text: sig.codice,
            font: "bold 14px sans-serif",
            declutterMode: isSelected ? "none" : "declutter",
          }),
          zIndex: zIndex - 1
        }))
      }*/

      // return styleOutput;
    }
  }

  getStyleSostegni() {
    return (feature: Feature, resolution: number) => {
      const sig = feature.getProperties()["src"] as Sostegno;
      const isSelected = this.selectionSostegni.indexOf(sig.codice) != -1;
      if (this.showOnlyActiveSostegni && !isSelected) {
        return null;
      }

      const symbol = new OlIcon({
        src: isSelected ? "/assets/icons/sostegno_selezionato.png" : "/assets/icons/sostegno.png",
        width: isSelected ? 48 : 24,
        anchor: [0.5, 1],
        declutterMode: "none"
      });

      // console.log("res", resolution);
      const zIndex = isSelected ? 15000: 5000;
      const hasText = isSelected || resolution < 0.5;

      const styleOutput = [
        new OlStyle({
        image: symbol,
        zIndex: zIndex,
        })
      ];

      if (hasText) {
        styleOutput.unshift(new OlStyle({
          text: new OlText({
            text: sig.codice,
            font: "bold 14px sans-serif",
            declutterMode: isSelected ? "none" : "declutter",
          }),
          zIndex: zIndex - 1
        }))
      }
      return styleOutput;
    }
  }

  getStylePositionPin () {
    return (feature: Feature, resolution: number) => {
      const symbol = new OlIcon({
        src: "/assets/icons/indirizzo.png",
        width: 48,
        anchor: [0.5, 1]
      });

      return new OlStyle({
        image: symbol,
      });
    }
  }

  getStyleEdifici() {
    return (feature: Feature, resolution: number) => {
      const edificio = feature.getProperties()["src"] as Edificio;
      const isSelected = this.selectionEdifici.indexOf(edificio.gid) != -1;
      let styleText = null;
      //TODO: avoid + 5, create a const
      if (this.mapview.getZoom() >= ActivitiesMapWidget.MIN_ZOOM_LABELS + 5 || (isSelected && this.mapview.getZoom() >= ActivitiesMapWidget.MIN_ZOOM_LABELS)) {
        styleText = new OlText({
          text: edificio?.codice || edificio?.indirizzo || "",
          font: "bold 8pt sans-serif",
          fill: new OlFill({
            color: isSelected ? "#3a0000" : "#1f2a1f"
          }),
          stroke: new OlStroke({
            width: 4,
            color: "rgba(255,255,255,0.95)"
          }),
          overflow: true
        });
      }

      return [
        new OlStyle({
          stroke: new OlStroke({
            color: isSelected ? "rgba(180,20,20,0.95)" : "rgba(249, 88, 12,0.95)",
            width: isSelected ? 4 : 3
          }),
          fill: new OlFill({
            color: isSelected ? "rgba(200,50,50,0.30)" : "rgba(249, 115, 22,0.15)"
          })
        }),
        new OlStyle({
          stroke: new OlStroke({
            color: "rgba(255,255,255,0.85)",
            width: 1
          }),
          text: styleText
        })
      ];
    }
  }


  notifyEvent(event: ActivitiesMapEvents, ...args: any[]) {
    for (let listener of this.listeners) {
      listener.onEvent(event, ...args);
    }
  }

  resetView () {
    this.mapview.fit(this.getViewExtent(), {
      callback: () => {
      }
    });

  }

  centerOnCoords (lon: number, lat: number) {
    const mapCoords = fromLonLat([lon, lat]);
    this.mapview.animate({
      center: mapCoords,
      zoom: 19
    });
  }

  // funzioni che possono essere chiamate anche da elementi ESTERNI e non generano quindi eventi di loro iniziativa

  positionToSostegno (codiceSostegno: string) {
    if (codiceSostegno != null) {
      // this.selectActivePosition(null);
      const sostFeature = this.sostegniLayer.getSource().getFeatures().find((feature) => {
        return feature.getProperties()["src"]["codice"] == codiceSostegno;
      });
      const sostegno: Sostegno = sostFeature.getProperties()["src"];
      this.centerOnCoords(sostegno.longitudine, sostegno.latitudine);
    }
  }

  /**
   * Imposta la selezione su un sostegno
   * @param codiceSostegno
   */
  toggleSelectElementSostegno(codiceSostegno: string) {
    const cidx = this.selectionSostegni.indexOf(codiceSostegno);
    if (cidx != -1) {
      this.selectionSostegni.splice(cidx, 1);
    } else {
      this.selectionSostegni.push(codiceSostegno);
    }

    this.sostegniLayer.changed();
    this.positionToSostegno(codiceSostegno);
  }

  toggleSelectElementEdificio(idEdificio: number) {
    const cidx = this.selectionEdifici.indexOf(idEdificio);
    if (cidx != -1) {
      this.selectionEdifici.splice(cidx, 1);
    } else {
      this.selectionEdifici.push(idEdificio);
    }
    this.edificiLayer.changed();
  }

  selectElementSostegni (codici: string[]) {
    this.selectionSostegni = [...codici];
    this.sostegniLayer.changed();
  }

  selectElementSostegno (codiceSostegno: string) {
    if (this.selectionSostegni.indexOf(codiceSostegno) == -1) {
      this.selectionSostegni.push(codiceSostegno);
      this.sostegniLayer.changed();
    }
    this.positionToSostegno(codiceSostegno);
  }

  /**
   * Imposta la selezione su un PIN
   * @param lonlat
   * @param doCenter
   */
  selectActivePosition(lonlat: number[], doCenter: boolean = true) {
    const source = this.positionLayer.getSource();
    source.clear();
    if (!!lonlat) {
      this.toggleSelectElementSostegno(null);
      const geom = new Point(lonlat);
      geom.applyTransform(this.transformDegWeb);
      const feature = new Feature(geom);
      source.addFeature(feature);
      source.changed();
      if (doCenter) {
        this.centerOnCoords(lonlat[0], lonlat[1]);
      }
    }
  }

  setVisibilitySegnalazioni (visible: boolean) {
    this.segnalazioniLayer.setVisible(visible);
  }

  setVisibilityInterventi (visible: boolean) {
    this.interventiLayer.setVisible(visible);
  }

  applyPosition (pos: PosizioneSegnalazione) {
    // console.log("apply position");
    this.selectElementSostegni((pos?.sostegni ?? []).map((sostegno) => { return sostegno.codice}));
    const hasOtherThanCoords = (pos?.sostegni ?? []).length > 0 && (!!pos?.coordinates);
    if (pos?.coordinates) {
        // console.log("apply coords");
        this.selectActivePosition([pos.coordinates.longitudine, pos.coordinates.latitudine], !hasOtherThanCoords);
    }

    if (pos.edifici) {
      this.selectionEdifici = pos.edifici.map((edificio) => edificio.gid);
      this.zoomToEdifici();
      return;
    }


    if (hasOtherThanCoords) {
      // console.log("recenter");
      this.zoomToPositionElements();
    }

  }

  zoomToPositionElements () {
    let elementsExtent = createEmpty();
    const activeFeatures = this.sostegniLayer.getSource().getFeatures().filter((feature) => {
      const sig = feature.getProperties()["src"] as Sostegno;
      return this.selectionSostegni.indexOf(sig.codice) != -1;
    })
    elementsExtent = boundingExtent([...activeFeatures, ...this.positionLayer.getSource().getFeatures()]);
    if (!isEmpty(elementsExtent)) {
      this.mapview.fit(elementsExtent, {
        padding: [64, 64],
        duration: 1000
      });
    }
  }

  zoomToEdifici() {
    const selectionSet = new Set(this.selectionEdifici);

    const edificiFeatures = this.edificiLayer.getSource().getFeatures()
    .filter(f => {
      const sig = f.getProperties()["src"] as Edificio;
      return sig && selectionSet.has(sig.gid);
    });

    const positionFeatures = this.positionLayer.getSource().getFeatures();

    const totalExtent = [...edificiFeatures, ...positionFeatures].reduce((extent, feature) => {
      const geometry = feature.getGeometry();
      if (!geometry) return extent;

      const featExtent = geometry.getExtent();
      return this.isValidExtent(featExtent) ? extend(extent, featExtent) : extent;
    }, createEmpty());

    if (!isEmpty(totalExtent) && this.isValidExtent(totalExtent)) {
      this.mapview.fit(totalExtent, {
        padding: [64, 64, 64, 64],
        maxZoom: 17,
        duration: 1000
      });
    }
  }

  /**
   * Verifica se un extent OpenLayers è valido e finito.
   * Spostata fuori per migliorare la leggibilità e le performance.
   */
  private isValidExtent(extent: number[]): boolean {
    return (
      !!extent &&
      extent.length === 4 &&
      extent.every(Number.isFinite) &&
      extent[0] <= extent[2] && // minX <= maxX
      extent[1] <= extent[3]    // minY <= maxY
    );
  }
}

export enum ActivitiesMapEvents {
  SELECT_POSITION,
  SELECT_SOSTEGNO,
  SELECT_SEGNALAZIONE,
  SELECT_INTERVENTO,
  SELECT_EDIFICIO,
  MOVE_STATE,
  ZOOM_CHANGE,
  LAYERS_CHANGE,
  READY_STATE
}

export class ActivitiesMapEventListener {

  onEvent(event: ActivitiesMapEvents, ...args: any[]) {
    let handler;
    if (event == ActivitiesMapEvents.SELECT_POSITION) {
      handler = this.onSelectPosizione;
    } else if (event == ActivitiesMapEvents.SELECT_SOSTEGNO) {
      handler = this.onSelectSostegno;
    } else if (event == ActivitiesMapEvents.SELECT_SEGNALAZIONE) {
      handler = this.onSelectSegnalazione;
    } else if (event == ActivitiesMapEvents.SELECT_INTERVENTO) {
      handler = this.onSelectInterventoSegnalazione;
    } else if (event == ActivitiesMapEvents.MOVE_STATE) {
      handler = this.onMoveStateChange;
    } else if (event == ActivitiesMapEvents.ZOOM_CHANGE) {
      handler = this.onZoomChange;
    } else if (event == ActivitiesMapEvents.LAYERS_CHANGE) {
      handler = this.onLayersChange;
    } else if (event == ActivitiesMapEvents.READY_STATE) {
      handler = this.onReadyStateChange;
    } else if (event == ActivitiesMapEvents.SELECT_EDIFICIO) {
      handler = this.onSelectEdificio;
    }
    handler(...args);
  }

  onLayersChange () {};

  onSelectPosizione(lonlat: number[]) {};

  onSelectSostegno(sostegno: Sostegno) {};

  onSelectSegnalazione(segnalazione: Segnalazione_Firestore) {};

  onSelectInterventoSegnalazione(intevento: InterventoSegnalazione_FireStore) {};

  onMoveStateChange (moving: boolean) {};

  onZoomChange (level: number) {};

  onReadyStateChange (ready: boolean) {};

  onSelectEdificio(edificio: Edificio) {};
}
